/*
 *  ************************************************************************
 *  *  © [2015 - 2020] Quintype Technologies India Private Limited
 *  *  All Rights Reserved.
 *  *************************************************************************
 */
import get from "lodash/get";
import PropTypes from "prop-types";
import React, { useEffect, useRef } from "react";
import PhotoStoryTemplate from "../../../arrow-components/src/components/Rows/StoryTemplates/PhotoStoryTemplates";
import LiveBlogStoryTemplate from "../../../arrow-components/src/components/Rows/StoryTemplates/LiveBlogStoryTemplates";
import TextStoryTemplate from "../../../arrow-components/src/components/Rows/StoryTemplates/TextStoryTemplates";
import ListicleStoryTemplate from "../../../arrow-components/src/components/Rows/StoryTemplates/ListicleStoryTemplates";
import VideoStoryTemplate from "../../../arrow-components/src/components/Rows/StoryTemplates/VideoStoryTemplates";
import { accessCheck, handleStoryCardsAccess } from "../../utils";
import { getAccessData } from "../sso-components/sso-utils";
import { AccessType, UpdateOnInterval } from "@quintype/components";
import PaywallBanner from "../basic/request-subscription/paywall-banner";
import { StoryFooter } from "../molecules/story-footer/index.js";
import { getStoryDataWithAdSlots } from "./get-story";

const PaywallStoryAccessWrapper = ({
  access,
  checkAccess,
  story,
  accessUpdated,
  accessIsLoading,
  accessLoading,
  config,
  initAccessType,
  templateConfig,
  preview,
  storyElementsConfig,
  adComponent,
  widgetComp
}) => {
  const gotAccessRef = useRef(false);

  async function getAccess() {
    gotAccessRef.current = true;
    const accessData = await getAccessData(checkAccess, story, accessUpdated, accessIsLoading, config);
    gotAccessRef.current = false;
    return accessData;
  }

  const checkAccessForStory = (story, access = {}) => {
    if (access) {
      return story.id in access;
    }
  };

  useEffect(() => {
    if (!accessLoading && !checkAccessForStory(story, access) && !gotAccessRef.current) {
      initAccessType(getAccess);
    }
  });

  const authorization = handleStoryCardsAccess(access, accessLoading, story.id, config);
  const paywallTemplateConfig = Object.assign({}, templateConfig, {
    noOfVisibleCards: templateConfig.pageBuilderVisibleCards || authorization.visibleCards
  });

  const wrapper = StoryComponent => ({
    story,
    paywallTemplateConfig,
    storyElementsConfig,
    adComponent,
    widgetComp
  }) => {
    return (
      <StoryComponent
        story={story}
        config={paywallTemplateConfig}
        storyElementsConfig={storyElementsConfig}
        adComponent={adComponent}
        widgetComp={widgetComp}
      >
        {authorization.isPaywallEnabled && <PaywallBanner story={story} />}
        {!preview && <StoryFooter story={story} />}
      </StoryComponent>
    );
  };

  const StoryTemplate = ({
    subPageType,
    story,
    paywallTemplateConfig,
    storyElementsConfig,
    adComponent,
    widgetComp
  }) => {
    switch (subPageType) {
      case "video":
        return wrapper(VideoStoryTemplate)({
          story,
          paywallTemplateConfig,
          storyElementsConfig,
          adComponent,
          widgetComp
        });
      case "photo":
        return wrapper(PhotoStoryTemplate)({
          story,
          paywallTemplateConfig,
          storyElementsConfig,
          adComponent,
          widgetComp
        });
      case "text":
        return wrapper(TextStoryTemplate)({
          story,
          paywallTemplateConfig,
          storyElementsConfig,
          adComponent,
          widgetComp
        });
      case "listicle":
        return wrapper(ListicleStoryTemplate)({
          story,
          paywallTemplateConfig,
          storyElementsConfig,
          adComponent,
          widgetComp
        });
      case "live-blog":
        return wrapper(LiveBlogStoryTemplate)({
          story,
          paywallTemplateConfig,
          storyElementsConfig,
          adComponent,
          widgetComp
        });
      default:
        return wrapper(TextStoryTemplate)({
          story,
          paywallTemplateConfig,
          storyElementsConfig,
          adComponent,
          widgetComp
        });
    }
  };

  const subPageType = story["story-template"];

  return StoryTemplate({ subPageType, story, paywallTemplateConfig, storyElementsConfig, adComponent, widgetComp });
};

PaywallStoryAccessWrapper.propTypes = {
  story: PropTypes.object,
  config: PropTypes.object,
  checkAccess: PropTypes.func,
  accessUpdated: PropTypes.func,
  initAccessType: PropTypes.func,
  accessIsLoading: PropTypes.func,
  initRazorPayPayment: PropTypes.func,
  getSubscriptionForUser: PropTypes.func,
  access: PropTypes.object,
  accessLoading: PropTypes.bool,
  templateConfig: PropTypes.object,
  preview: PropTypes.bool,
  storyElementsConfig: PropTypes.object,
  adComponent: PropTypes.func,
  widgetComp: PropTypes.func
};

const PaywallStory = ({
  member,
  config,
  story,
  access = {},
  templateConfig,
  storyElementsConfig,
  adComponent,
  widgetComp,
  storyBaseType
}) => {
  const email = get(member, ["email"], "");
  const phone = get(member, ["metadata", "phone-number"], "");

  const themeAttributes = get(config, ["theme-attributes"]);
  const PbAccessTypeConfig = get(config, ["pagebuilder-config", "general", "accesstypeIntegration"]);
  const accessTypeKey = get(PbAccessTypeConfig, ["accesstypeKey"], get(themeAttributes, ["accesstypeKey"]));
  const accessTypeBkIntegrationId = get(
    PbAccessTypeConfig,
    ["bridgeKeeperIntegrationId"],
    get(themeAttributes, ["accesstypeBkIntegrationId"], "")
  );
  const isStaging =
    get(PbAccessTypeConfig, ["accesstypeMode"]) === "staging" ||
    get(themeAttributes, ["accesstypeEnvironment"]) === "staging";
  const enableAccesstype = get(
    PbAccessTypeConfig,
    ["enableAccesstype"],
    get(themeAttributes, ["enableAccesstype"], false)
  );
  const disableMetering = get(
    PbAccessTypeConfig,
    ["disableMetering"],
    get(themeAttributes, ["disableMetering"]) === "true"
  );
  const subPageType = storyBaseType || story["story-template"];

  const storyId = get(story, ["id"], "");
  const { storySlots } = templateConfig;
  const storyDataLoader = () => getStoryDataWithAdSlots(storyId, storySlots);

  const accessTypeProdHost = get(PbAccessTypeConfig, ["accessTypeProdHost"]);

  const accessTypeStgHost = get(PbAccessTypeConfig, ["accessTypeStgHost"]);

  return (
    <AccessType
      enableAccesstype={accessCheck(enableAccesstype)}
      isStaging={isStaging}
      accessTypeKey={accessTypeKey} // "9rm1ijqQmGfJ2kwQ9zt2gWhK"
      accessTypeBkIntegrationId={accessTypeBkIntegrationId}
      email={email}
      phone={phone}
      disableMetering={disableMetering}
      storyId={story.id}
      prodHost={accessTypeProdHost}
      stagingHost={accessTypeStgHost}
    >
      {({
        initAccessType,
        checkAccess,
        accessUpdated,
        accessIsLoading,
        initRazorPayPayment,
        getSubscriptionForUser
      }) => {
        if (subPageType === "live-blog") {
          return (
            <UpdateOnInterval dataLoader={storyDataLoader} interval={20000} initData={{ story: story }}>
              {({ data }) => {
                const { story } = data;
                return (
                  <PaywallStoryAccessWrapper
                    accessIsLoading={accessIsLoading}
                    accessUpdated={accessUpdated}
                    initAccessType={initAccessType}
                    checkAccess={checkAccess}
                    initRazorPayPayment={initRazorPayPayment}
                    getSubscriptionForUser={getSubscriptionForUser}
                    story={story}
                    config={config}
                    access={access}
                    templateConfig={templateConfig}
                    storyElementsConfig={storyElementsConfig}
                    adComponent={adComponent}
                    widgetComp={widgetComp}
                  />
                );
              }}
            </UpdateOnInterval>
          );
        } else {
          return (
            <PaywallStoryAccessWrapper
              accessIsLoading={accessIsLoading}
              accessUpdated={accessUpdated}
              initAccessType={initAccessType}
              checkAccess={checkAccess}
              initRazorPayPayment={initRazorPayPayment}
              getSubscriptionForUser={getSubscriptionForUser}
              story={story}
              config={config}
              access={access}
              templateConfig={templateConfig}
              storyElementsConfig={storyElementsConfig}
              adComponent={adComponent}
              widgetComp={widgetComp}
            />
          );
        }
      }}
    </AccessType>
  );
};

PaywallStory.propTypes = {
  story: PropTypes.object,
  config: PropTypes.object,
  member: PropTypes.object,
  access: PropTypes.object,
  templateConfig: PropTypes.object,
  storyElementsConfig: PropTypes.object,
  adComponent: PropTypes.func,
  widgetComp: PropTypes.func,
  storyBaseType: PropTypes.string
};

export default PaywallStory;
